import React, { useMemo } from 'react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../store/store';
import type { DesignData } from '../../../../@types/types';
import DashboardModal from './DashboardModal';
import './oneButton-css/DesignHistoryModal.css';

function formatSystemSizeKw(kw: number | undefined | null): string {
    if (kw == null || !Number.isFinite(Number(kw))) return '—';
    return `${Number(kw).toFixed(2)} kW`;
}

function formatCreatedAt(iso: string | undefined | null): string {
    if (iso == null || String(iso).trim() === '') return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

export interface DesignHistoryModalProps {
    open: boolean;
    onClose: () => void;
}

export const DesignHistoryModal: React.FC<DesignHistoryModalProps> = ({ open, onClose }) => {
    const theme = useOneButtonStore((s) => s.theme);
    const designList = useViewerStore((s) => s.designList) as DesignData[];
    const selectedDesignIndex = useViewerStore((s) => s.selectedDesignIndex);

    const rows = useMemo(() => {
        if (!Array.isArray(designList) || designList.length === 0) return [];
        return designList.map((d, index) => {
            return {
            key: `${d.DesignID}-${d.Version_No}-${index}`,
            index,
            designNo: d.Design_No ?? '—',
            versionNo: d.Version_No ?? '—',
            modules:d.ModulesCount ?? '—',
            systemSize: formatSystemSizeKw(d.System_Size_kWdc),
            createdAt: formatCreatedAt(d.User_Created_Timestamp),
            isCurrent: index === selectedDesignIndex,
        };
        });
    }, [designList, selectedDesignIndex]);

    return (
        <DashboardModal
            open={open}
            title="Design history - OneDraw"
            onClose={onClose}
            footer={null}
            width={700}
            centered
            maskClosable={false}
            theme={theme}
            destroyOnClose
            className={`obd-design-history-modal obd-theme-${theme}`}
            rootClassName={`obd-design-history-modal-root obd-theme-${theme}`}
        >
            <div className="obd-design-history-modal__body">
                {rows.length === 0 ? (
                    <p className="obd-design-history-modal__empty">No saved designs loaded for this site yet.</p>
                ) : (
                    <table className="obd-design-history-table">
                        <thead>
                            <tr>
                                <th scope="col">Design #</th>
                                <th scope="col">Version #</th>
                                <th scope="col">Modules</th>
                                <th scope="col">System size</th>
                                <th scope="col">Created</th>
                                <th scope="col" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr
                                    key={r.key}
                                    className={r.isCurrent ? 'obd-design-history-table__row--current' : undefined}
                                >
                                    <td>{r.designNo}</td>
                                    <td>{r.versionNo}</td>
                                    <td>{r.modules}</td>
                                    <td>{r.systemSize}</td>
                                    <td>{r.createdAt}</td>
                                    <td>
                                        {r.isCurrent ? (
                                            <span className="obd-design-history-table__badge">Current</span>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </DashboardModal>
    );
};

export default DesignHistoryModal;
